import {
  CustomField,
  getFormattedAddress,
  updatePhoneValueSearch,
} from '@sales-center/common'
import { DraftLead } from '@user/store/draftLeads/typings'
import { IServiceLocation } from '@user/store/locations/typings'
import { ITargetPest, pestNamesToIds } from '@user/store/targetPests'
import { createInitialValues, ILeadFormModel } from '../typings'
import { getInitialValuesForLocation } from '.'
import { NoteCode } from '@user/components/Notes/typings'
import { IEmployee } from '@user/store/employee/typings'
import { FIELD_MAX_LENGTHS } from '../functions/fieldLimits'

const trunc = (value: string | null | undefined, max: number): string =>
  (value || '').slice(0, max)

export function getDraftInitialValues(
  customFields: CustomField[],
  draft: DraftLead,
  allTargetPests: ITargetPest[],
  serviceLocation?: IServiceLocation,
  noteCodes?: NoteCode[],
  employees?: IEmployee[],
  addNewlocation?: boolean,
): ILeadFormModel {
  const targetPestInput: string[] = pestNamesToIds(
    allTargetPests,
    draft.targetPests,
  )

  const getEmpValue = (userName: string) => {
    const emp = employees?.find((x) => x.username === userName)
    return emp ? `${emp.firstName} ${emp.lastName}` : ''
  }

  const getEmpId = (userName: string) => {
    const emp = employees?.find((x) => x.username === userName)
    return emp ? emp.id : ''
  }

  if (serviceLocation) {
    return {
      ...getInitialValuesForLocation(
        customFields,
        serviceLocation,
        null,
        noteCodes,
      ),
      draftLeadId: draft.id,
      type: draft.type,
      draftNote: draft.note || '',
      title: draft.title || '',
      submittedBy: draft.userName,
      submittedDate: draft.createdTime,
      referral:
        draft.referringBillTo === null
          ? draft.type !== 'Mobile'
            ? null
            : {
                referralSource: '',
                referralValue: getEmpValue(draft.submittedBy),
                referralDiscountCode: '',
                referralDiscountId: '',
                referralDiscountValue: 0.0,
                referralType: 'Employee',
                referralLocationCode: '',
                referralEmployeeId: getEmpId(draft.submittedBy),
              }
          : {
              referralSource: '',
              referralValue: draft.referringBillTo,
              referralDiscountCode: draft.referralCode,
              referralDiscountId: draft.discountCodeId,
              referralDiscountValue: 0.0,
              referralType: 'Customer',
              referralLocationCode: '',
              referralEmployeeId: null,
            },

      targetPests: targetPestInput,
      salesTeamId: draft.salesTeamId === null ? '' : draft.salesTeamId,
      salesFunnelId: draft.salesFunnelId === null ? '' : draft.salesFunnelId,
      assigneeId: draft.assigneeId === null ? '' : draft.assigneeId,
      campaignId: draft.campaignId || '',
      leadSourceId: draft.sourceId || '',
      campaign: draft.campaign || '',
      leadSource: draft.source || '',
      importId: draft.importId || 0,
      importName: draft.importName,
    }
  } else {
    const emailsSplit = draft?.emailAddress
      ? draft.emailAddress.includes(',')
        ? draft?.emailAddress.split(',')
        : draft?.emailAddress.split(';')
      : null

    const usableEmails = emailsSplit?.splice(0, 3)
    return {
      ...createInitialValues(
        customFields,
        draft.customFieldValues || [],
        undefined,
        addNewlocation,
      ),

      firstName: trunc(draft.firstName, FIELD_MAX_LENGTHS.firstName),
      lastName: trunc(draft.lastName, FIELD_MAX_LENGTHS.lastName),
      title: draft.title || '',
      company: '',
      emailAddresses: usableEmails
        ? usableEmails.map((e, index) => ({
            emailAddress: e,
            isPrimary: index === 0,
          }))
        : [{ emailAddress: '', isPrimary: true }],
      phoneNumber: draft.phoneNumber
        ? updatePhoneValueSearch(draft.phoneNumber)
        : '',
      phoneExt: trunc(draft.phoneExt, FIELD_MAX_LENGTHS.phoneExt),
      mobilePhone: '',
      altPhoneNumber: draft.altPhoneNumber
        ? updatePhoneValueSearch(draft.altPhoneNumber)
        : '',
      altPhoneExt: trunc(draft.altPhoneExt, FIELD_MAX_LENGTHS.altPhoneExt),
      address1: trunc(draft.street1, FIELD_MAX_LENGTHS.street),
      address2: trunc(draft.street2, FIELD_MAX_LENGTHS.street),
      city: draft.city || '',
      state: trunc(draft.stateAbbreviation, FIELD_MAX_LENGTHS.state),
      postalCode: trunc(draft.postalCode, FIELD_MAX_LENGTHS.postalCode),
      fullAddress:
        draft.street1 &&
        draft.city &&
        draft.stateAbbreviation &&
        draft.postalCode
          ? getFormattedAddress(
              draft.street1 || '',
              draft.street2 || '',
              draft.city || '',
              draft.stateAbbreviation || '',
              draft.postalCode || '',
            )
          : '',
      county: '',
      latitude: 0,
      longitude: 0,
      externalLinkId: '',
      externalLinkType: '',
      contactTime: '',
      contactMethod: '',

      billingLocation: false,
      division: draft.division || '',
      divisionId: draft.divisionId || '',
      locationTypeId: draft.locationTypeId || '',
      locationType: draft.locationType || '',
      primaryServiceLocation: false,
      //endregion

      //region BillTo Location
      billTo_firstName: '',
      billTo_lastName: '',
      billTo_title: '',
      billTo_company: '',
      billTo_emailAddresses: [],
      billTo_phoneNumber: '',
      billTo_phoneExt: '',
      billTo_mobilePhone: '',
      billTo_altPhoneNumber: '',
      billTo_altPhoneExt: '',
      billTo_address1: '',
      billTo_address2: '',
      billTo_city: '',
      billTo_state: '',
      billTo_postalCode: '',
      billTo_county: '',
      billTo_latitude: 0,
      billTo_longitude: 0,
      billTo_externalLinkId: '',
      billTo_externalLinkType: '',
      billTo_contactTime: '',
      billTo_contactMethod: '',
      billTo_billingLocation: true,
      billTo_division: '',
      billTo_divisionId: '',
      billTo_locationTypeId: '',
      billTo_locationType: '',
      billTo_primaryServiceLocation: true,

      branch: draft.branch || '',
      branchId: draft.branchId || '',
      campaignId: draft.campaignId || '',
      country: draft.country || '',
      draftLeadId: draft.id,
      type:
        draft.type === null || draft.type.length === 0 ? 'Manual' : draft.type,

      leadSourceId: draft.sourceId || '',
      lat: draft.latitude || 0,
      lng: draft.longitude || 0,
      note: '',
      draftNote: draft.note || '',
      externalLocationId: draft.locationId || '',
      submittedBy: draft.userName,
      submittedDate: draft.createdTime,
      referral:
        draft.referringBillTo === null
          ? draft.type !== 'Mobile'
            ? null
            : {
                referralSource: '',
                referralValue: getEmpValue(draft.submittedBy),
                referralDiscountCode: '',
                referralDiscountId: '',
                referralDiscountValue: 0.0,
                referralType: 'Employee',
                referralLocationCode: '',
                referralEmployeeId: getEmpId(draft.submittedBy),
              }
          : {
              referralSource: '',
              referralValue: draft.referringBillTo,
              referralDiscountCode: draft.referralCode,
              referralDiscountId: draft.discountCodeId,
              referralDiscountValue: 0.0,
              referralType: 'Customer',
              referralLocationCode: '',
              referralEmployeeId: null,
            },
      targetPests: targetPestInput,
      salesTeamId: draft.salesTeamId === null ? '' : draft.salesTeamId,
      salesFunnelId: draft.salesFunnelId === null ? '' : draft.salesFunnelId,
      assigneeId: draft.assigneeId === null ? '' : draft.assigneeId,
      campaign: draft.campaign || '',
      leadSource: draft.source || '',
      webFormCallTrackingEventId: draft.webFormCallTrackingEventId,
      locationSameAsBillTo: true,
      importId: draft.importId,
      importName: draft.importName,
    }
  }
}
